import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import { notify, confirmDialog } from "../../components/Notify";

export default function OpsIMessage() {
  const token = localStorage.getItem("userToken");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [agents, setAgents] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState("");
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const flash = (m, type = "success") => notify(m, type);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API_BASE_URL}/ops/imessage/agents`, { headers }),
        axios.get(`${API_BASE_URL}/ops/companies`, { headers }),
      ]);
      setAgents(a.data.agents || []);
      setCompanies(c.data || []);
    } catch (err) {
      console.error("OpsIMessage load:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const createToken = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    setCreating(true);
    setNewToken(null);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/ops/imessage/agents`,
        { companyId, label },
        { headers }
      );
      setNewToken(data.token);
      setLabel("");
      flash("Pairing token created.");
      load();
    } catch (err) {
      flash(err.response?.data?.error || "Failed to create token.", "error");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    const ok = await confirmDialog({
      title: "Revoke this Mac?",
      message: "It will be cut off immediately and can no longer send or receive.",
      danger: true,
      okText: "Revoke",
    });
    if (!ok) return;
    try {
      await axios.post(`${API_BASE_URL}/ops/imessage/agents/${id}/revoke`, {}, { headers });
      flash("Mac revoked.");
      load();
    } catch (err) {
      flash("Failed to revoke.", "error");
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(newToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusBadge = (s) => {
    const map = {
      online: { bg: "rgba(37,211,102,0.14)", c: "#7de8a8", t: "Online" },
      awaiting_apple_id: { bg: "rgba(245,181,68,0.16)", c: "#f5cd83", t: "Awaiting Apple ID" },
      offline: { bg: "rgba(239,90,90,0.16)", c: "#ff9d9d", t: "Offline" },
      revoked: { bg: "rgba(255,255,255,0.08)", c: "#aab0c4", t: "Revoked" },
    };
    const v = map[s] || map.offline;
    return <span style={{ background: v.bg, color: v.c, padding: "3px 10px", borderRadius: 100, fontSize: ".75rem", fontWeight: 600 }}>{v.t}</span>;
  };

  return (
    <div>

      {/* Setup resources */}
      <div className="card mb-4">
        <div className="card-body d-flex flex-wrap align-items-center gap-2">
          <div className="me-auto">
            <h6 className="mb-1">Mac setup resources</h6>
            <div className="small text-muted">Download the installer and send the guide to whoever sets up the Mac.</div>
          </div>
          <a
            className="btn btn-primary"
            href="https://github.com/saif-ullah-k/pairfon-installer/releases/download/v2/PairfonInstaller.zip"
          >
            <i className="bi bi-download me-1" /> Download Installer
          </a>
          <a
            className="btn btn-outline-primary"
            href={`${process.env.PUBLIC_URL || ""}/imessage-guide.html`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="bi bi-book me-1" /> Install Guide
          </a>
        </div>
      </div>

      {/* New Mac */}
      <div className="card mb-4">
        <div className="card-body">
          <h6 className="mb-3">Provision a Mac</h6>
          <form onSubmit={createToken} className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label small">Company</label>
              <select className="form-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} required>
                <option value="">Select company…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small">Label (Mac name)</label>
              <input className="form-control" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Mac-Office-1" />
            </div>
            <div className="col-md-3">
              <button className="btn btn-primary w-100" disabled={creating || !companyId}>
                {creating ? "Creating…" : "Generate Pairing Token"}
              </button>
            </div>
          </form>

          {newToken && (
            <div className="mt-3 p-3" style={{ background: "rgba(255,255,255,0.03)", borderRadius: 10, border: "1px solid var(--pf-border)" }}>
              <div className="small text-muted mb-1">One-time pairing token — paste it into <b>Pairfon Installer.app</b> on the Mac:</div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <input
                  readOnly
                  value={newToken}
                  onFocus={(e) => e.target.select()}
                  className="form-control form-control-sm"
                  style={{ fontFamily: "monospace" }}
                />
                <button
                  className="btn btn-sm btn-outline-secondary"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={copyToken}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <div className="small text-muted" style={{ wordBreak: "break-all" }}>
                Advanced (CLI): <span style={{ fontFamily: "monospace" }}>CORE_URL=https://api.pairfon.com PAIRING_TOKEN={newToken} node pairfon-agent.js</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fleet */}
      <div className="card">
        <div className="card-body">
          <h6 className="mb-3">iMessage Fleet</h6>
          {loading ? (
            <p className="text-muted">Loading…</p>
          ) : agents.length === 0 ? (
            <p className="text-muted mb-0">No Macs enrolled yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Company</th><th>Label</th><th>Apple ID</th><th>Status</th><th>Last Seen</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a) => (
                    <tr key={a.id}>
                      <td>{a.company_name || a.company_id?.slice(0, 8)}</td>
                      <td>{a.label || "—"}</td>
                      <td>{a.handle || <span className="text-muted">—</span>}</td>
                      <td>{statusBadge(a.status)}</td>
                      <td className="text-muted small">{a.last_heartbeat_at ? new Date(a.last_heartbeat_at).toLocaleString() : "—"}</td>
                      <td className="text-end">
                        {a.status !== "revoked" && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => revoke(a.id)}>Revoke</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
